'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useLocale } from 'next-intl'
import { Suspense } from 'react'
import { T } from '@/components/t'
import { FooterItem } from '@/features/footer/footer-item'
import { FooterLanguageSection } from '@/features/footer/footer-language-section'
import { FooterSection } from '@/features/footer/footer-section'
import { Locale } from '@/setup/locales'
import { getLinkHref, usePathname } from '@/setup/routing'
import { ExternalDeploymentUrl } from '@/setup/urls/external-deployment-urls'

export const Footer = () => {
    const locale = useLocale()
    const pathname = usePathname()

    const isDutch = locale === Locale.NL_NL || locale === Locale.EN_NL

    if (pathname.startsWith('/sumup')) {
        return null
    }

    return (
        <footer className="min-w-full max-w-full w-full flex items-center justify-center border-t-black/20 border-t-[1px] py-16">
            <div className="flex flex-col justify-center lg:max-w-[1040px] max-w-full w-full items-center gap-y-14 px-5">
                <div className="flex md:flex-row flex-col gap-y-14 justify-center w-full">
                    <div className="flex flex-col md:flex-row gap-y-8 w-full">
                        <Suspense>
                            <FooterLanguageSection />
                        </Suspense>

                        <FooterSection title={<T t="footer.about" />}>
                            <FooterItem
                                label={<T t="footer.for-companies" />}
                                href={getLinkHref(
                                    {
                                        pattern: '/for-companies',
                                        params: undefined,
                                    },
                                    locale as Locale
                                )}
                            />

                            <FooterItem
                                label={<T t="footer.contact" />}
                                href={getLinkHref(
                                    { pattern: '/contact', params: undefined },
                                    locale as Locale
                                )}
                            />

                            <FooterItem
                                label={<T t="footer.jobs" />}
                                href={getLinkHref(
                                    { pattern: '/jobs', params: undefined },
                                    locale as Locale
                                )}
                            />

                            <FooterItem
                                label={<T t="footer.faq" />}
                                href={getLinkHref(
                                    { pattern: '/faq', params: undefined },
                                    locale as Locale
                                )}
                            />

                            <FooterItem
                                label={<T t="footer.becomeAPartner" />}
                                href={getLinkHref(
                                    { pattern: '/become-a-partner', params: undefined },
                                    locale as Locale
                                )}
                            />

                            <FooterItem
                                label={<T t="footer.partnerSupport" />}
                                href={ExternalDeploymentUrl.PARTNER_SUPPORT[locale as Locale]}
                            />

                            {locale === Locale.EN_GB && (
                                <FooterItem
                                    label="Student Discount"
                                    href={'https://neotaste.com/gb/special-offer'}
                                />
                            )}

                            <FooterItem
                                label={<T t="footer.experiences" />}
                                href={getLinkHref(
                                    { pattern: '/experiences', params: undefined },
                                    locale as Locale
                                )}
                            />

                            {!isDutch && (
                                <FooterItem
                                    label={<T t="footer.shop" />}
                                    href={ExternalDeploymentUrl.SHOP[locale as Locale]}
                                />
                            )}
                        </FooterSection>
                    </div>

                    <div className="flex flex-col md:flex-row gap-y-14 w-full">
                        <FooterSection title={<T t="footer.legal" />}>
                            <FooterItem
                                label={<T t="footer.imprint" />}
                                href={getLinkHref(
                                    { pattern: '/imprint', params: undefined },
                                    locale as Locale
                                )}
                            />

                            <FooterItem
                                label={<T t="footer.privacyPolicy" />}
                                href={getLinkHref(
                                    { pattern: '/privacy-policy', params: undefined },
                                    locale as Locale
                                )}
                            />

                            <FooterItem
                                label={<T t="footer.cookies" />}
                                href="/#"
                                isCookieTrigger
                            />

                            <FooterItem
                                label={<T t="footer.termsAndConditions" />}
                                href={getLinkHref(
                                    { pattern: '/terms-and-conditions', params: undefined },
                                    locale as Locale
                                )}
                            />
                        </FooterSection>

                        <FooterSection title={<T t="footer.social" />}>
                            <FooterItem
                                label="Instagram"
                                href="https://instagram.com/neotaste.app"
                            />

                            <FooterItem
                                label="TikTok"
                                href="https://www.tiktok.com/@neotaste.app"
                            />

                            <FooterItem label="Facebook" href="https://facebook.com/neotaste.app" />

                            <FooterItem
                                label="LinkedIn"
                                href="https://www.linkedin.com/company/neotaste"
                            />
                        </FooterSection>
                    </div>
                </div>

                <Link
                    href={getLinkHref({ pattern: '/', params: undefined }, locale as Locale)}
                    className="min-h-[32px] min-w-[155px]"
                >
                    <Image
                        src="/_images/navigation/neotaste-navigation-logo.svg"
                        alt="NeoTaste Logo"
                        height={26}
                        width={124}
                        priority
                        className="min-h-[26px] min-w-[124px]"
                    />
                </Link>
            </div>
        </footer>
    )
}
